<script>
import ShopEventBus from '@/components/shop-modules/shop-event-bus';
import {
  addMultipleProductsToCartFunction,
  addProductToCartFunction,
  checkMfvCodesFunction,
  getCartFunction,
  getCartIdFunction,
  getCurrentCartIdFunction,
  updateShoppingcartCounterFunction,
} from '@/composables/shoppingcart';
import ADDMULTIPLEPRODUCTSTOCART from '@/graphql/addMultipleProductsToCart.gql';
import ADDPRODUCTTOCART from '@/graphql/addProductToCart.gql';
import DELETECART from '@/graphql/deleteCart.gql';
import DELETECARTPOSITIONS from '@/graphql/deleteCartPositions.gql';
import DELETEPRODUCT from '@/graphql/deleteProduct.gql';
import GETCART from '@/graphql/getCart.gql';
import GETIDSCARTASXML from '@/graphql/getIdsCartAsXML.gql';
import UPDATEQUANTITY from '@/graphql/updateQuantity.gql';
import eventHubMixin from '@/mixins/eventHubMixin.vue';
import trackingMixin from '@/mixins/trackingMixin.vue';
import userMixin from '@/mixins/userMixin.vue';
import { getIsIds } from '@/utils/idsTools';

export default {
  mixins: [userMixin, eventHubMixin, trackingMixin],
  emits: ['get-cart-code'],
  data() {
    return {
      isAddingItemToCart: {},
      cart: {},
      mfvCode: [],
      deliveryAddressId: undefined,
      cartErrors: null,
    };
  },
  computed: {
    getProductDetailsInCart() {
      return false;
    },
    isAddingToCart() {
      return Object.keys(this.isAddingItemToCart).length;
    },
    invalidAccessToken: {
      get() {
        return this.$store.getters['cart/invalidToken'];
      },
    },
  },
  watch: {
    async invalidAccessToken(newValue) {
      if (newValue) {
        await this.logoutIfTokenInvalid({ message: 'GraphQL error: Invalid token' });
      }
    },
  },
  created() {
    this.$store.dispatch('cart/initialize', {
      apollo: this.$apollo,
      fischer: this.$fischer,
    });
  },
  methods: {
    async getShoppingCart() {
      if (!this.$fischer.cartId()) {
        await this.getCart();
      }
      return this.$fischer.cartId();
    },
    async getCartId() {
      return getCartIdFunction(this.useCartCode, this.cartCode, this.$emit, this.$fischer, () =>
        this.getShoppingCart(),
      );
    },
    async getCurrentCartId(noWarning = false) {
      return getCurrentCartIdFunction(
        this.$fischer,
        (...args) => this.getCart(...args),
        () => this.cart,
        noWarning,
      );
    },
    enterErrorState(...errors) {
      // Note: do not do anything here, this is most likely overwritten by the consumer
      console.error(...errors);
    },
    async trackCartUpdate(startTime, csvProducts) {
      if (startTime) {
        const simulateTime = (Date.now() - startTime) / 1000;
        await this.sendEvent('custom', {}, 'cart-update-time', {
          taken: simulateTime,
          products: this.getCartProducts().length,
        });
      }
      if (csvProducts) {
        const productsIds = csvProducts.map((element) => element.productID);
        const addedProducts = this.getCartProducts().filter((product) => {
          // TODO: not uploaded
          if (productsIds.includes(product.articleNo)) {
            return product;
          }
        });

        const items = addedProducts.map((product) => {
          return {
            productId: product.articleNo,
            quantity: product.quantity,
            productPrice: product.totalPrice.value,
            currency: product.totalPrice.currencyIso,
          };
        });
        const cartId = await this.getCurrentCartId();
        const content = {
          cartId: cartId,
          items: items,
          actionType: 'csv_upload',
        };

        await this.sendEvent('add_to_cart', content);
      }
    },
    async removeCartPositions(products) {
      try {
        this.isUpdating = true;
        this.haveErrors = false;
        const startTime = Date.now();

        const cartId = await this.getCartId();
        const orderEntries = products.map((i) => ({
          entryNumber: parseInt(i.id, 10),
        }));

        await this.$apollo.mutate({
          mutation: DELETECARTPOSITIONS,
          variables: {
            baseSiteId: this.$fischer.baseSiteId(),
            accessToken: this.$fischer.user.accessToken(),
            entries: JSON.stringify({ orderEntries }),
            isIds: getIsIds(),
            cartId,
            userId: this.$fischer.user.id(),
            lang: this.$fischer.language_639_1(),
          },
        });

        this.trackRemoveFromCart(products);

        await this.getCart();
        this.trackCartUpdate(startTime);

        this.isUpdating = false;
        this.quantityChanged = false;

        this.$store.dispatch('notifications/openToastNotification', {
          name: 'product-removed',
          message: {
            value: this.$dict.get('online-Shop.Global-Labels.Notifications.All-Products-Removed'),
          },
          type: 'success',
          icon: 'check',
          close: true,
          delayLong: true,
        });
      } catch (error) {
        this.enterErrorState(error);
      }
    },
    async removeProduct(product) {
      try {
        const startTime = Date.now();
        this.isUpdating = true;
        this.haveErrors = false;
        const cartId = await this.getCartId();
        await this.$apollo.mutate({
          mutation: DELETEPRODUCT,
          variables: {
            baseSiteId: this.$fischer.baseSiteId(),
            accessToken: this.$fischer.user.accessToken(),
            id: parseInt(product.id, 10),
            isIds: getIsIds(),
            cartId,
            userId: this.$fischer.user.id(),
            lang: this.$fischer.language_639_1(),
          },
        });

        this.trackRemoveFromCart([product]);

        await this.getCart();
        this.trackCartUpdate(startTime);

        this.isUpdating = false;
        this.quantityChanged = false;

        this.$store.dispatch('notifications/openToastNotification', {
          name: 'product-removed',
          message: {
            value: this.$dict.get('online-Shop.Global-Labels.Notifications.Product-Removed'),
          },
          type: 'success',
          icon: 'check',
          close: true,
          delayLong: true,
        });
      } catch (error) {
        this.enterErrorState(error);
      }
    },
    async addProductToCart({
      productID,
      productRequestedQuantity = 0,
      productInitialQuantity,
      disableNotification,
      rethrowErrors,
      targetCart,
      disableTracking = false,
      fromMemoList,
      actionType,
    }) {
      return addProductToCartFunction(
        {
          productID,
          productRequestedQuantity,
          productInitialQuantity,
          disableNotification,
          rethrowErrors,
          targetCart,
          disableTracking,
          fromMemoList,
        },
        this.isAddingItemToCart,
        (value) => (this.isUpdating = value),
        () => this.getCartId(),
        (variables) =>
          this.$apollo.mutate({
            mutation: ADDPRODUCTTOCART,
            errorPolicy: 'all',
            variables,
          }),
        this.$fischer,
        (...errors) => this.enterErrorState(...errors),
        this.$store,
        this.$dict,
        () => this.cart,
        (cart) => (this.cart = cart),
        () => this.notifyCartChange(),
        (noWarning) => {
          return this.getCurrentCartId(noWarning);
        },
        (trackingCartId, productID, quantity, fromMemoList) =>
          this.trackAddToCart(trackingCartId, productID, quantity, fromMemoList, actionType),
      );
    },
    async addMultipleProductsToCart({
      products,
      createNewCart = false,
      disableNotification,
      rethrowErrors,
      noTracking,
    }) {
      return addMultipleProductsToCartFunction(
        {
          products,
          createNewCart,
          disableNotification,
          rethrowErrors,
          noTracking,
        },
        {
          enterErrorState: (type, errors) => this.enterErrorState(type, errors),
          fischer: this.$fischer,
          getCartId: () => this.getCartId(),
          getCurrentCartId: () => this.getCurrentCartId(),
          isAddingItemToCart: this.isAddingItemToCart,
          mutate: (variables) =>
            this.$apollo.mutate({
              mutation: ADDMULTIPLEPRODUCTSTOCART,
              errorPolicy: 'all',
              variables,
            }),
          notifyCartChange: () => this.notifyCartChange(),
          setIsUpdating: (value) => (this.isUpdating = value),
          trackAddMultipleToCart: (...args) => this.trackAddMultipleToCart(...args),
        },
      );
    },
    async notifyCartChange(noCartSnackBar) {
      try {
        const newCartPositionsCount = await this.getCartPositionsCount();

        if (!noCartSnackBar) {
          ShopEventBus.$emit('ProductAddToCart', true);
          ShopEventBus.$emit('ShoppingcartCounter', newCartPositionsCount);
        }

        this.updateShoppingcartCounter(newCartPositionsCount);
      } catch (error) {
        this.enterErrorState('an error has occurred, notifyCartChange', error);
      }
    },
    async getCartPositionsCount() {
      if (IS_SSR) {
        return 0;
      }
      await this.$store.dispatch('cart/refetchPositionsCount');
      return this.$store.getters['cart/positionsCount'] || 0;
    },
    checkMfvCodes(errors) {
      checkMfvCodesFunction(errors, (codes) => (this.mfvCode = codes), this.$store, this.$fischer);
    },
    async getCart(showLoading = true, noOrderSimulateParam = false, noWarning = false) {
      return getCartFunction(
        showLoading,
        noOrderSimulateParam,
        noWarning,
        this.$dict,
        this.$fischer,
        () => this.mfvCode,
        (error) => this.logoutIfTokenInvalid(error),
        async () => {
          await this.$nextTick();
          if (this.$refs.mfvErrorMessage) {
            this.$refs.mfvErrorMessage.trigger();
          } else {
            // eslint-disable-next-line no-console
            console.log('Could not notify mfv-code', this.mfvCode);
          }
        },
        (variables) =>
          this.$apollo.query({
            query: GETCART,
            fetchPolicy: 'no-cache',
            cachePolicy: 'no-cache',
            variables,
          }),
        (cart) => (this.cart = cart),
        (value) => (this.deliveryAddressId = value),
        (value) => (this.haveErrors = value),
        (value) => (this.isLoading = value),
        (value) => (this.isUpdating = value),
        (codes) => (this.mfvCode = codes),
        this.$store,
      );
    },
    async deleteCart() {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: DELETECART,
          variables: {
            baseSiteId: this.$fischer.baseSiteId(),
            accessToken: this.$fischer.user.accessToken(),
            cartId: this.cartCode || this.$fischer.cartId(),
            userId: this.$fischer.user.id(),
          },
        });

        if (!data?.deleteCart) {
          this.$store.dispatch('notifications/openGeneralErrorNotification', { vm: this });
          console.error('Could not delete cart');
        }

        return data?.deleteCart;
      } catch (error) {
        this.$store.dispatch('notifications/openGeneralErrorNotification', { vm: this });
        console.error('Could not delete cart', error);
        return false;
      }
    },
    updateShoppingcartCounter(amount) {
      updateShoppingcartCounterFunction(this.$store, amount);
    },
    getCartProducts() {
      const products = this.savedCart ? this.savedCart.products : this.cart?.products;
      return products || [];
    },
    async updateQuantity(qty, productId) {
      const startTime = Date.now();
      const quantity = parseInt(qty);
      const productIdInt = parseInt(productId, 10);
      const products = this.getCartProducts();
      const product = products[productIdInt] || {};
      const oldQuantity = parseInt(product.quantity, 10);
      if (oldQuantity !== quantity) {
        try {
          this.isUpdating = true;
          this.haveErrors = false;
          const { data } = await this.$apollo.mutate({
            mutation: UPDATEQUANTITY,
            variables: {
              baseSiteId: this.$fischer.baseSiteId(),
              accessToken: this.$fischer.user.accessToken(),
              id: productIdInt,
              quantity: quantity > 0 ? quantity : 1,
              isIds: getIsIds(),
              cartId: this.cartCode || this.$fischer.cartId(),
              userId: this.$fischer.user.id(),
            },
          });
          if (data.updateQuantity) {
            await this.getCart();
            this.trackCartUpdate(startTime);
          }
        } catch (error) {
          await this.logoutIfTokenInvalid(error);
          this.enterErrorState(error);
          return;
        }
      }
      this.isUpdating = false;
      this.quantityChanged = false;
    },
    async sendCartToIds(cartInstance) {
      this.isUpdating = true;
      try {
        if (!cartInstance) {
          throw new Error('missing cart instance');
        }
        const { data } = await this.$apollo.query({
          query: GETIDSCARTASXML,
          variables: {
            baseSiteId: this.$fischer.baseSiteId(),
            accessToken: this.$fischer.user.accessToken(),
          },
        });

        await this.postXMLToIds(data.getIdsCartAsXML.xml, cartInstance);
      } catch (error) {
        this.enterErrorState('Error sending order', error);
      }
    },
    async postXMLToIds(xml, cartInstance) {
      return cartInstance.postCartToIDS(xml);
    },
  },
};
</script>
